/**
 * Handler per la gestione dei grafici Chart.js
 * Compatibile con Vue.js - non utilizza tag <script> nei template
 */
export default class ChartsHandler {
    
    constructor() {
        this.charts = new Map(); // Mappa per tenere traccia dei grafici creati
        this.init();
    }
    
    init() {
        // Verifica che jQuery sia disponibile
        if (typeof $ === 'undefined' || typeof jQuery === 'undefined') {
            setTimeout(() => this.init(), 100);
            return;
        }
        
        // Verifica che Chart.js sia disponibile
        if (typeof Chart === 'undefined') {
            // Aspetta che Chart.js sia caricato o caricalo dinamicamente
            this.waitForChartJS(() => {
                this.initializeCharts();
            });
        } else {
            this.initializeCharts();
        }
    }
    
    waitForChartJS(callback, attempts = 0) {
        const maxAttempts = 50; // 5 secondi massimo
        
        if (typeof Chart !== 'undefined') {

            callback();
            return;
        }
        
        if (attempts >= maxAttempts) {
            console.warn('⚠️ Chart.js non trovato dopo', maxAttempts * 100, 'ms, carico dinamicamente');
            this.loadChartJS(callback);
            return;
        }
        
        // Aspetta ancora un po'
        setTimeout(() => {
            this.waitForChartJS(callback, attempts + 1);
        }, 100);
    }
    
    loadChartJS(callback) {
        // Carica Chart.js dinamicamente se non è già presente
        if (document.querySelector('script[src*="chart.js"]')) {
            callback();
            return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
        script.onload = callback;
        document.head.appendChild(script);
    }
    
    initializeCharts() {
        // Configura il font globale per tutti i grafici Chart.js
        if (typeof Chart !== 'undefined' && Chart.defaults) {
            Chart.defaults.font = {
                family: "'Sofia Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                size: 12,
                weight: '400'
            };
        }
        
        // Cerca tutti gli elementi con data attributes per i grafici
        const chartElements = document.querySelectorAll('[data-chart-config]');
        

        
        if (chartElements.length === 0) {
            return;
        }
        
        chartElements.forEach((element) => {
            this.createChart(element);
        });
        

        
        // Osserva i cambiamenti nel DOM per nuovi grafici
        this.observeNewCharts();
    }
    
    observeNewCharts() {
        // Crea un observer per nuovi elementi con data-chart-config
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Controlla se il nodo stesso ha data-chart-config
                        if (node.hasAttribute && node.hasAttribute('data-chart-config')) {
                            this.createChart(node);
                        }
                        // Controlla i figli del nodo
                        const childCharts = node.querySelectorAll ? node.querySelectorAll('[data-chart-config]') : [];
                        childCharts.forEach(child => {
                            this.createChart(child);
                        });
                    }
                });
            });
        });
        
        // Inizia ad osservare
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        

    }
    
    createChart(element) {
        try {
            if (!element.dataset.chartConfig) {
                return;
            }
            
            const chartConfig = JSON.parse(element.dataset.chartConfig);
            const chartName = element.id;
            
            if (!chartName) {
                return;
            }
            
            // Verifica che Chart.js sia disponibile
            if (typeof Chart === 'undefined') {
                return;
            }
            
            // Processa le funzioni JavaScript nelle opzioni
            this.processFunctions(chartConfig);
            
            // Distruggi il grafico esistente se presente
            if (this.charts.has(chartName)) {
                this.charts.get(chartName).destroy();
            }
            
            // Aggiungi handler per click se il grafico è cliccabile
            const clickable = element.dataset.chartClickable;
            if (clickable) {
                chartConfig.options = chartConfig.options || {};
                chartConfig.options.onClick = (event, elements, chart) => {
                    this.handleChartClick(event, elements, chart, clickable);
                };
            }
            
            // Crea il nuovo grafico
            const chart = new Chart(element, chartConfig);
            this.charts.set(chartName, chart);
            
        } catch (error) {
            console.error('Errore nella creazione del grafico:', error);
        }
    }
    
    /**
     * Gestisce il click su una barra/elemento del grafico
     * @param {Event} event - Evento click
     * @param {Array} elements - Elementi cliccati
     * @param {Chart} chart - Istanza del grafico
     * @param {string} clickable - Tipo di navigazione (es. "milestones")
     */
    handleChartClick(event, elements, chart, clickable) {
        if (!elements || elements.length === 0) {
            return;
        }
        
        const element = elements[0];
        const dataIndex = element.index;
        const datasetIndex = element.datasetIndex; // Indice del dataset (categoria)
        const label = chart.data.labels[dataIndex];
        
        // Ottieni il nome del dataset per identificare la categoria
        const dataset = chart.data.datasets[datasetIndex];
        const datasetLabel = dataset ? (dataset.label || '') : '';
        
        if (!label) {
            return;
        }
        
        // Label è nel formato "01/2025" (mese/anno)
        const parts = label.split('/');
        if (parts.length !== 2) {
            console.warn('Formato label non valido:', label);
            return;
        }
        
        const month = parseInt(parts[0], 10);
        const year = parseInt(parts[1], 10);
        
        if (isNaN(month) || isNaN(year)) {
            console.warn('Impossibile parsare mese/anno dalla label:', label);
            return;
        }
        
        // Calcola primo e ultimo giorno del mese
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0); // Giorno 0 del mese successivo = ultimo giorno del mese corrente
        
        // Formatta le date nel formato richiesto dalla datatable: "dd/mm/yyyy HH:mm"
        const formatDate = (date, isEnd = false) => {
            const day = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            const time = isEnd ? '23:59' : '00:00';
            return `${day}/${m}/${y} ${time}`;
        };
        
        const startDateStr = formatDate(firstDay, false);
        const endDateStr = formatDate(lastDay, true);
        const dateFilter = `${startDateStr} - ${endDateStr}`;
        
        // Determina la categoria basata sul datasetIndex
        // 0 = Fatturato, 1 = Confermati, 2 = Pipeline, 3 = Anno precedente
        let category = null;
        if (datasetIndex === 0) {
            category = 'invoiced'; // Fatturato
        } else if (datasetIndex === 1) {
            category = 'confirmed'; // Confermati (non ancora fatturati)
        } else if (datasetIndex === 2) {
            category = 'pipeline'; // Pipeline
        }
        // datasetIndex === 3 è "Anno precedente" - non ha categoria speciale
        
        // Naviga alla pagina appropriata
        if (clickable === 'milestones') {
            this.navigateToMilestones(dateFilter, category);
        }
    }
    
    /**
     * Naviga alla pagina milestones con il filtro data preimpostato
     * @param {string} dateFilter - Filtro data nel formato "dd/mm/yyyy HH:mm - dd/mm/yyyy HH:mm"
     * @param {string|null} category - Categoria milestone: 'invoiced', 'confirmed', 'pipeline', o null
     */
    navigateToMilestones(dateFilter, category = null) {
        // Salva il filtro nel localStorage PRIMA di navigare
        // Questo persiste anche dopo eventuali redirect
        try {
            localStorage.setItem('brain_chart_date_filter', dateFilter);
            localStorage.setItem('brain_chart_date_filter_timestamp', Date.now().toString());
            if (category) {
                localStorage.setItem('brain_chart_category', category);
            } else {
                localStorage.removeItem('brain_chart_category');
            }
        } catch (e) {
            console.warn('Impossibile salvare nel localStorage:', e);
        }
        
        // Costruisci URL con parametri
        let url = '/milestones?date_filter=' + encodeURIComponent(dateFilter);
        if (category) {
            url += '&category=' + encodeURIComponent(category);
        }
        
        // Naviga alla pagina milestones
        window.location.href = url;
    }
    
    // Metodo per processare le funzioni JavaScript nelle configurazioni
    processFunctions(obj) {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (typeof obj[key] === 'string' && obj[key].startsWith('function')) {
                    // Converte la stringa in funzione
                    try {
                        obj[key] = new Function('return ' + obj[key])();
                    } catch (e) {
                        console.warn('Impossibile convertire la funzione:', obj[key], e);
                    }
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                    // Ricorsione per oggetti annidati (come segment, borderDash, etc.)
                    this.processFunctions(obj[key]);
                }
            }
        }
    }
    
    // Metodo per creare un grafico orizzontale (usato dai template)
    static createHorizontalChart(chartName, data, options = {}) {
        const element = document.getElementById(chartName);
        if (!element) {
            console.error('Elemento canvas non trovato:', chartName);
            return null;
        }
        
        const config = {
            type: 'bar',
            data: data,
            options: {
                animation: false,
                responsive: true,
                aspectRatio: options.aspectRatio || 4,
                scales: {
                    x: {
                        stacked: options.stacked || false
                    },
                    y: {
                        stacked: options.stacked || false
                    }
                },
                plugins: {
                    title: {
                        display: !!options.title,
                        text: options.title || ''
                    }
                }
            }
        };
        
        // Salva la configurazione nell'elemento per uso futuro
        element.dataset.chartConfig = JSON.stringify(config);
        
        return new Chart(element, config);
    }
    
    // Metodo per distruggere tutti i grafici
    destroyAllCharts() {
        this.charts.forEach(chart => {
            chart.destroy();
        });
        this.charts.clear();
    }
    
    // Metodo per ottenere un grafico specifico
    getChart(chartName) {
        return this.charts.get(chartName);
    }
    
    // Metodo per reinizializzare tutti i grafici (utile per debugging)
    reinitializeAllCharts() {
        this.destroyAllCharts();
        this.initializeCharts();
    }
    
    // Metodo per ottenere statistiche sui grafici
    getStats() {
        const stats = {
            totalCharts: this.charts.size,
            chartNames: Array.from(this.charts.keys()),
            chartTypes: {}
        };
        
        this.charts.forEach((chart, name) => {
            const type = chart.config.type;
            stats.chartTypes[type] = (stats.chartTypes[type] || 0) + 1;
        });
        
        return stats;
    }
    

}

// Esponi il ChartsHandler globalmente per debugging
window.ChartsHandler = ChartsHandler;
